import React, { useEffect, useState } from "react";
import { Star, X } from "lucide-react";
import { createPortal } from "react-dom";
import api from "../../api";
import DealCard from "./DealCard";
import { useLang } from "../../context/LanguageContext";

// What a shop is actually buying: their own card, sitting in the real home Featured row.
export const FeaturedPreview = ({ deal, onClose, onBuy, localPrice = 10, globalPrice = 25, busy }) => {
  const { t } = useLang();
  const [reach, setReach] = useState("local");
  const [slot, setSlot] = useState(null);
  const [cal, setCal] = useState(null);
  const [date, setDate] = useState(null);  // null = feature now; else a future YYYY-MM-DD
  const mock = { ...deal, featured: true };
  const price = reach === "global" ? globalPrice : localPrice;

  useEffect(() => {
    setSlot(null); setCal(null); setDate(null);
    api.get("/featured/slots", { params: { deal_id: deal.id, reach } }).then((r) => setSlot(r.data)).catch(() => setSlot(null));
    api.get("/featured/calendar", { params: { deal_id: deal.id, reach } }).then((r) => setCal(r.data)).catch(() => setCal(null));
  }, [deal.id, reach]);
  const full = slot && slot.remaining <= 0 && !slot.mine;
  const selDay = date ? (cal?.days || []).find((x) => x.date === date) : null;
  const blocked = date ? !!(selDay && selDay.taken && !selDay.mine) : full;

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6"
      onClick={onClose} data-testid="featured-preview">
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
        className="bg-card w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl border border-border shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[88vh] overflow-hidden">
        <div className="shrink-0 flex items-start gap-3 px-5 sm:px-6 pt-5 pb-4 border-b border-border">
          <div className="flex-1 min-w-0">
            <h3 className="text-xl md:text-lg font-extrabold text-foreground">{t("fp.title")}</h3>
            <p className="text-sm text-muted-foreground mt-1">{t("fp.honest")}</p>
            {slot && (
              <p className={`text-xs font-semibold mt-1.5 ${full ? "text-[#b8860b]" : "text-[#16a34a]"}`} data-testid="featured-slots-line">
                {full
                  ? t("fp.full", { date: (slot.nextFreeAt || "").slice(0, 10) })
                  : t("fp.slots")}
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="featured-preview-close" aria-label={t("bo.close")}
            className="shrink-0 h-9 w-9 -mt-1 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 sm:px-6 py-5">
          <div className="flex gap-2 mb-4" data-testid="featured-reach-toggle">
            <button type="button" onClick={() => setReach("local")} data-testid="featured-reach-local"
              className={`flex-1 rounded-xl border px-3 py-2.5 text-left transition-colors ${reach === "local" ? "border-[#16a34a] bg-[#16a34a]/10" : "border-border hover:bg-secondary"}`}>
              <span className="block text-sm font-bold text-foreground">{t("fp.reachLocal")}</span>
              <span className="block text-xs text-muted-foreground">${localPrice} · {t("fp.reachLocalNote")}</span>
            </button>
            <button type="button" onClick={() => setReach("global")} data-testid="featured-reach-global"
              className={`flex-1 rounded-xl border px-3 py-2.5 text-left transition-colors ${reach === "global" ? "border-[#16a34a] bg-[#16a34a]/10" : "border-border hover:bg-secondary"}`}>
              <span className="block text-sm font-bold text-foreground">{t("fp.reachGlobal")}</span>
              <span className="block text-xs text-muted-foreground">${globalPrice} · {t("fp.reachGlobalNote")}</span>
            </button>
          </div>
          <div className="mb-4" data-testid="featured-date-picker">
            <p className="text-xs font-semibold text-foreground mb-2">{t("fp.pickDay")}</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button type="button" onClick={() => setDate(null)} data-testid="featured-day-today"
                className={`shrink-0 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${date === null ? "border-[#16a34a] bg-[#16a34a]/10 text-foreground" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                {t("fp.today")}
              </button>
              {(cal?.days || []).map((x) => {
                const dis = x.taken && !x.mine;
                const lab = new Date(x.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric" });
                return (
                  <button key={x.date} type="button" disabled={dis} onClick={() => setDate(x.date)} data-testid={`featured-day-${x.date}`}
                    className={`shrink-0 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${dis ? "border-border text-muted-foreground/40 line-through cursor-not-allowed" : date === x.date ? "border-[#16a34a] bg-[#16a34a]/10 text-foreground" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                    {lab}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="rounded-2xl border border-dashed border-[#16a34a]/40 bg-secondary/40 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-extrabold text-foreground">{t("home.featuredNearYou")}</h4>
              <span className="text-xs font-semibold text-muted-foreground">{t("home.viewAll")}</span>
            </div>
            <div className="max-w-sm" data-testid="featured-preview-card">
              <DealCard deal={mock} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4">{t("fp.note")}</p>
        </div>

        <div className="shrink-0 flex items-center justify-end gap-2 px-5 sm:px-6 py-4 border-t border-border">
          <button onClick={onClose} data-testid="featured-preview-cancel" className="text-sm font-semibold px-4 py-2.5 rounded-xl border border-border hover:bg-secondary transition-colors">{t("fp.notNow")}</button>
          <button onClick={() => onBuy(reach, date)} disabled={!!busy || blocked} data-testid="featured-preview-buy"
            className="text-sm font-bold px-5 py-2.5 rounded-xl bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white transition-colors flex items-center gap-2">
            <Star size={15} /> {t("fp.buy", { price: `$${price}` })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FeaturedPreview;
